/* eslint-disable no-unused-vars */
import { type Money } from '../common/common'
import { type DisclosureInput } from '../transactions/transactions'

export enum InvestmentAccountType {
  // BROKERAGE
  BROKERAGE = 'BROKERAGE',
  COMPANION_BROKERAGE = 'COMPANION_BROKERAGE', // Only FE references, BE does not have these references

  // ROTH
  ROTH_IRA = 'ROTH_IRA',
  ROTH_IRA_INHERITED = 'ROTH_IRA_INHERITED',
  COMPANION_ROTH_IRA = 'COMPANION_ROTH_IRA', // Only FE references, BE does not have these references

  // TRADITIONAL
  TRADITIONAL_IRA = 'TRADITIONAL_IRA',
  TRADITIONAL_IRA_INHERITED = 'TRADITIONAL_IRA_INHERITED',
  COMPANION_IRA = 'COMPANION_IRA', // Only FE references, BE does not have these references

  // JOINT
  JOINT_BROKERAGE_TIC = 'JOINT_BROKERAGE_TIC',
  JOINT_BROKERAGE_JTWROS = 'JOINT_BROKERAGE_JTWROS',
  COMPANION_JOINT_BROKERAGE_TIC = 'COMPANION_JOINT_BROKERAGE_TIC',
  COMPANION_JOINT_BROKERAGE_JTWROS = 'COMPANION_JOINT_BROKERAGE_JTWROS',
  JOINT_BROKERAGE_TBTE = 'JOINT_BROKERAGE_TBTE', // TENANTS BY THE ENTIRETY
  JOINT_BROKERAGE_CP = 'JOINT_BROKERAGE_CP', // COMMUNITY PROPERTY
  COMPANION_JOINT_BROKERAGE_TBTE = 'JOINT_BROKERAGE_TBTE_COMPANION', // TENANTS BY THE ENTIRETY COMPANION
  COMPANION_JOINT_BROKERAGE_CP = 'JOINT_BROKERAGE_CP_COMPANION', // COMMUNITY PROPERTY COMPANION
}

export enum IraDistributionType {
  PREMATURE = 'PREMATURE',
  QUALIFIED = 'QUALIFIED',
  RMD = 'RMD',
}

export interface IraWithdrawalAttribute {
  withdrawalAmount: number
  distributionType: IraDistributionType
}

export interface InvestmentAccountAttributes {
  minimumDeposit?: number
  minimumDepositMet?: boolean
  iraWithdrawal?: IraWithdrawalAttribute
  acatAllowed: boolean
  clientWithdrawalAllowed: boolean
  clientDepositAllowed: boolean
}

export interface InvestmentAccount {
  alias: number
  accountNumber: string
  accountType: InvestmentAccountType
  accountName: string
  attributes: InvestmentAccountAttributes
  status: InvestmentAccountStatus
}

export interface InvestmentAccountBalance {
  accountValue: Money
  investedBalance: Money
  cashBalance: Money
  pendingWithdrawal: Money
  availableForWithdrawal: Money
  amountSinceInception: Money
}

export enum EquitiesSector {
  HEALTHCARE = 'HEALTHCARE',
  CONSUMER = 'CONSUMER',
  FINANCIAL = 'FINANCIAL',
  TECHNOLOGY = 'TECHNOLOGY',
  INDUSTRY = 'INDUSTRY',
  ENERGY = 'ENERGY',
  REAL_ESTATE = 'REAL_ESTATE',
  MEDIA = 'MEDIA',
  OTHER = 'OTHER',
}

export interface InvestmentHolding {
  amount: Money
  quantity: number
  costBasis?: Money
  symbol: string
  name: string
}

export interface InvestmentEquitiesSector {
  sector: EquitiesSector
  percentage: number
  amount: Money
  holdings: InvestmentHolding[]
}

export interface InvestmentEquitiesAsset {
  __typename: 'InvestmentEquitiesAsset'
  amount: Money
  percentage: number
  holdings: InvestmentHolding[]
  sectors: InvestmentEquitiesSector[] // @deprecated # DW-only
}

export enum FixedIncomeCategory {
  GOVERNMENT_BONDS = 'GOVERNMENT_BONDS',
  HIGH_YIELD_BONDS = 'HIGH_YIELD_BONDS',
  CORPORATE_BONDS = 'CORPORATE_BONDS',
  MUNICIPAL_BONDS = 'MUNICIPAL_BONDS',
  INTERNATIONAL_BONDS = 'INTERNATIONAL_BONDS',
}

export interface InvestmentFixedIncomeCategory {
  amount: Money
  percentage: number
  category: FixedIncomeCategory
  holdings: InvestmentHolding[]
}

export interface InvestmentFixedIncomeAsset {
  __typename: 'InvestmentFixedIncomeAsset'
  amount: Money
  percentage: number
  holdings: InvestmentHolding[]
  categories: InvestmentFixedIncomeCategory[] // @deprecated # DW-only
}

export interface InvestmentCashEquivalentCategory {
  amount: Money
  percentage: number
  category: CashEquivalentCategory
  holdings: InvestmentHolding[]
}

export interface InvestmentCashEquivalentAsset {
  __typename: 'InvestmentCashEquivalentAsset'
  amount: Money
  percentage: number
  holdings: InvestmentHolding[]
  categories: InvestmentCashEquivalentCategory[] // @deprecated # DW-only
}

export interface InvestmentOtherAsset {
  __typename: 'InvestmentOtherAsset'
  amount: Money
  percentage: number
  holdings: InvestmentHolding[]
}

export interface InvestmentAssetAllocation {
  __typename: 'InvestmentAssetAllocation'
  preference: InvestmentAssetAllocationBreakdown
  equities: InvestmentEquitiesAsset
  fixedIncome: InvestmentFixedIncomeAsset
  cashEquivalent: InvestmentCashEquivalentAsset
  other: InvestmentOtherAsset
}

export interface RetirementContribution {
  annualContributionsLimit: number
  remainingAnnualLimit: number
}

export interface InvestmentAccountDetailBase {
  __typename: string
  alias: number
  accountNumber: string
  accountName: string
  balance: InvestmentAccountBalance
  status: InvestmentAccountStatus
  accountTitle?: string
}
export interface InvestmentBrokerageAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentBrokerageAccount'
  attributes: InvestmentAccountAttributes
  assetAllocation: InvestmentAssetAllocation
}
export interface InvestmentRothIRAAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentRothIRAAccount'
  attributes: InvestmentAccountAttributes
  assetAllocation: InvestmentAssetAllocation
  retirementContribution: RetirementContribution
}
export interface InvestmentIRAAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentIRAAccount'
  attributes: InvestmentAccountAttributes
  assetAllocation: InvestmentAssetAllocation
  retirementContribution: RetirementContribution
}

export interface InvestmentBrokerageCompanionAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentBrokerageCompanionAccount'
  attributes?: InvestmentAccountAttributes
  holdings: InvestmentHolding[]
}

export interface InvestmentIRACompanionAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentIRACompanionAccount'
  holdings: InvestmentHolding[]
}

export interface InvestmentRothIRACompanionAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentRothIRACompanionAccount'
  holdings: InvestmentHolding[]
}

export interface InvestmentInheritedIRAAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentInheritedIRAAccount'
  attributes: InvestmentAccountAttributes
  assetAllocation: InvestmentAssetAllocation
}

export interface InvestmentInheritedRothIRAAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentInheritedRothIRAAccount'
  attributes: InvestmentAccountAttributes
  assetAllocation: InvestmentAssetAllocation
}

export interface InvestmentJointBrokerageTenantsInCommonAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentJointBrokerageTenantsInCommonAccount'
  attributes: InvestmentAccountAttributes
  assetAllocation: InvestmentAssetAllocation
}

export interface InvestmentJointBrokerageTenantsInCommonCompanionAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentJointBrokerageTenantsInCommonCompanionAccount'
  attributes: InvestmentAccountAttributes
  holdings: InvestmentHolding[]
}

export interface InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount'
  attributes: InvestmentAccountAttributes
  assetAllocation: InvestmentAssetAllocation
}

export interface InvestmentJointBrokerageTenantsWithRightOfSurvivorshipCompanionAccount
  extends InvestmentAccountDetailBase {
  __typename: 'InvestmentJointBrokerageTenantsWithRightOfSurvivorshipCompanionAccount'
  attributes: InvestmentAccountAttributes
  holdings: InvestmentHolding[]
}

export interface InvestmentJointBrokerageTenantsByEntiretyAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentJointBrokerageTenantsByEntiretyAccount'
  attributes: InvestmentAccountAttributes
  assetAllocation: InvestmentAssetAllocation
}

export interface InvestmentJointBrokerageTenantsByEntiretyCompanionAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentJointBrokerageTenantsByEntiretyCompanionAccount'
  attributes: InvestmentAccountAttributes
  holdings: InvestmentHolding[]
}

export interface InvestmentJointBrokerageCommunityPropertyAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentJointBrokerageCommunityPropertyAccount'
  attributes: InvestmentAccountAttributes
  assetAllocation: InvestmentAssetAllocation
}

export interface InvestmentJointBrokerageCommunityPropertyCompanionAccount extends InvestmentAccountDetailBase {
  __typename: 'InvestmentJointBrokerageCommunityPropertyCompanionAccount'
  attributes: InvestmentAccountAttributes
  holdings: InvestmentHolding[]
}

export type InvestmentAccountDetail =
  | InvestmentBrokerageAccount
  | InvestmentRothIRAAccount
  | InvestmentIRAAccount
  | InvestmentBrokerageCompanionAccount
  | InvestmentIRACompanionAccount
  | InvestmentRothIRACompanionAccount
  | InvestmentInheritedIRAAccount
  | InvestmentInheritedRothIRAAccount
  | InvestmentJointBrokerageTenantsInCommonAccount
  | InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount
  | InvestmentJointBrokerageTenantsInCommonCompanionAccount
  | InvestmentJointBrokerageTenantsWithRightOfSurvivorshipCompanionAccount
  | InvestmentJointBrokerageTenantsByEntiretyAccount
  | InvestmentJointBrokerageCommunityPropertyAccount
  | InvestmentJointBrokerageTenantsByEntiretyCompanionAccount
  | InvestmentJointBrokerageCommunityPropertyCompanionAccount

export interface BaseInvestmentInstrument {
  name: string
  description?: string // @deprecated # DW-only
  symbol?: string
  priorClosePrice?: Money
  image?: string // @deprecated # DW-only
  amountPerShare?: Money
}

export interface EquitiesInstrument extends BaseInvestmentInstrument {
  __typename: 'EquitiesInstrument'
  sector: EquitiesSector
}

export interface MutualFundInstrument extends BaseInvestmentInstrument {
  __typename: 'MutualFundInstrument'
}

export interface ETFInstrument extends BaseInvestmentInstrument {
  __typename: 'ETFInstrument'
}

export interface OtherInstrument extends BaseInvestmentInstrument {
  __typename: 'OtherInstrument'
}

export type InvestmentInstrument = EquitiesInstrument | MutualFundInstrument | ETFInstrument | OtherInstrument

export enum InvestmentTransferType {
  DEPOSIT = 'DEPOSIT',
  WITHDRAWAL = 'WITHDRAWAL',
}

export enum InvestmentTransferStatus {
  PROCESSING_FUNDS = 'PROCESSING_FUNDS',
  FUNDED = 'FUNDED',
  PROCESSING_TRADES = 'PROCESSING_TRADES',
  TRADES_COMPLETE = 'TRADES_COMPLETE',
}

export enum InvestmentAmountType {
  NOTIONAL = 'NOTIONAL',
  NOMINAL = 'NOMINAL',
}

export enum InvestmentTradeStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum InvestmentTradeSide {
  BUY = 'BUY',
  SELL = 'SELL',
}

export interface InvestmentActivityTrade {
  tradeId: number
  displayName: string
  symbol: string
  amount: number
  amountType?: InvestmentAmountType
  status: InvestmentTradeStatus
  side: InvestmentTradeSide
}

export interface BaseInvestmentActivityDetailV2 {
  __typename:
    | 'InvestmentTransferActivityDetailV2'
    | 'InvestmentDividendActivityDetailV2'
    | 'InvestmentFeeActivityDetail'
    | 'InvestmentInterestActivityDetail'
    | 'InvestmentAcatGroupActivityDetailV2'
  activityId: number
  displayName: string
  date: string
  amount?: Money
}

export type InvestmentActivityDetailV2 =
  | InvestmentTransferActivityDetailV2
  | InvestmentDividendActivityDetailV2
  | InvestmentFeeActivityDetail
  | InvestmentInterestActivityDetail
  | InvestmentAcatGroupActivityDetailV2

export enum AcatDirection {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}

export interface BaseInvestmentActivityAcat {
  __typename: 'InvestmentActivityAcatStock' | 'InvestmentActivityAcatCash'
  displayName: string
  direction: AcatDirection
  amount?: Money
}

export interface InvestmentActivityAcatStock extends BaseInvestmentActivityAcat {
  __typename: 'InvestmentActivityAcatStock'
  quantity: number
  symbol: string
  instrumentName?: string
}

export interface InvestmentActivityAcatCash extends BaseInvestmentActivityAcat {
  __typename: 'InvestmentActivityAcatCash'
}

export type InvestmentActivityAcat = InvestmentActivityAcatStock | InvestmentActivityAcatCash

export interface InvestmentAcatGroupActivityDetailV2 extends BaseInvestmentActivityDetailV2 {
  __typename: 'InvestmentAcatGroupActivityDetailV2'
  sourceAccount: string
  destinationAccount: string
  transfers: InvestmentActivityAcat[]
}

export interface InvestmentTransferActivityDetailV2 extends BaseInvestmentActivityDetailV2 {
  __typename: 'InvestmentTransferActivityDetailV2'
  initiatedAmount: Money
  settledAmount?: Money // @deprecated # DW-only
  transferType: InvestmentTransferType
  transferStatus: InvestmentTransferStatus
  counterparty: string
  accountName: string
  initiatedDate: string
  completedDate?: string // @deprecated # DW-only
  trades: InvestmentActivityTrade[]
}

export interface InvestmentDividendActivityDetailV2 extends BaseInvestmentActivityDetailV2 {
  __typename: 'InvestmentDividendActivityDetailV2'
  amountPerShare: Money
  symbol: string
  instrumentName: string
  trades: InvestmentActivityTrade[]
}

export interface InvestmentInterestActivityDetail extends BaseInvestmentActivityDetailV2 {
  __typename: 'InvestmentInterestActivityDetail'
  trades: InvestmentActivityTrade[]
}

export interface InvestmentFeeActivityDetail extends BaseInvestmentActivityDetailV2 {
  __typename: 'InvestmentFeeActivityDetail'
  initiatedDate: string
  completedDate?: string // @deprecated # DW-only
  trades?: InvestmentActivityTrade[]
}

export interface InvestmentAccountActivityInput {
  alias: number
}

export interface InvestmentAccountActivity {
  activity: InvestmentActivityDetailV2[]
}

export interface InvestmentTradeDetail {
  __typename: 'InvestmentTradeDetail'
  tradeId: number
  displayName: string
  amountCash: Money
  quantity: number
  instrument?: InvestmentInstrument
  status: InvestmentTradeStatus
  executedTime?: string
}

export enum InvestmentTradeDetailErrorType {
  NOT_FOUND = 'NOT_FOUND',
}

export interface InvestmentTradeDetailError {
  __typename: 'InvestmentTradeDetailError'
  errorType: InvestmentTradeDetailErrorType
}

export type InvestmentTradeDetailOutput = InvestmentTradeDetail | InvestmentTradeDetailError

export interface InvestmentTradeDetailInput {
  tradeId: string
}

export interface InvestmentsTradeDetailInput {
  alias: number
  tradeId: string
}

export interface UpdateInvestmentAccountNameInput {
  alias: number
  accountName: string
}

export type UpdateInvestmentAccountNameOutput = UpdateInvestmentAccountName | UpdateInvestmentAccountNameError

export interface UpdateInvestmentAccountName {
  __typename: 'UpdateInvestmentAccountName'
  isSuccessful: boolean
}

export interface UpdateInvestmentAccountNameError {
  __typename: 'UpdateInvestmentAccountNameError'
  errorType: UpdateInvestmentAccountNameErrorType
}

export enum UpdateInvestmentAccountNameErrorType {
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  ACCOUNT_NAME_TOO_LONG = 'ACCOUNT_NAME_TOO_LONG',
  INVALID_ARGUMENT = 'INVALID_ARGUMENT',
}

// Risk Tolernace
export enum InvestmentRiskToleranceCategory {
  CONSERVATIVE_1 = 'CONSERVATIVE_1',
  CONSERVATIVE_2 = 'CONSERVATIVE_2',
  CONSERVATIVE_3 = 'CONSERVATIVE_3',
  CONSERVATIVE_4 = 'CONSERVATIVE_4',
  CONSERVATIVE_5 = 'CONSERVATIVE_5',
  MODERATE_1 = 'MODERATE_1',
  MODERATE_2 = 'MODERATE_2',
  MODERATE_3 = 'MODERATE_3',
  MODERATE_4 = 'MODERATE_4',
  MODERATE_5 = 'MODERATE_5',
  BALANCED_1 = 'BALANCED_1',
  BALANCED_2 = 'BALANCED_2',
  BALANCED_3 = 'BALANCED_3',
  BALANCED_4 = 'BALANCED_4',
  BALANCED_5 = 'BALANCED_5',
  GROWTH_1 = 'GROWTH_1',
  GROWTH_2 = 'GROWTH_2',
  GROWTH_3 = 'GROWTH_3',
  GROWTH_4 = 'GROWTH_4',
  GROWTH_5 = 'GROWTH_5',
  AGGRESSIVE_1 = 'AGGRESSIVE_1',
  AGGRESSIVE_2 = 'AGGRESSIVE_2',
  AGGRESSIVE_3 = 'AGGRESSIVE_3',
  AGGRESSIVE_4 = 'AGGRESSIVE_4',
  AGGRESSIVE_5 = 'AGGRESSIVE_5',
}

export interface InvestmentAssetAllocationBreakdown {
  equities: number
  fixedIncome: number
  cashEquivalent: number
}

export interface InvestmentRiskToleranceDetail {
  index: number
  category: InvestmentRiskToleranceCategory
  allocation: InvestmentAssetAllocationBreakdown
}

export interface InvestmentAssetAllocationRecommendationOutput {
  currentRiskTolerance: InvestmentRiskToleranceCategory
  currentAssetAllocationBreakdown: InvestmentAssetAllocationBreakdown
  recommendedRiskTolerance: InvestmentRiskToleranceCategory
  allowedRiskTolerances?: InvestmentRiskToleranceCategory[]
  riskTolerances: InvestmentRiskToleranceDetail[]
}

export interface UserInvestmentAssetAllocationConfig {
  riskTolerance: InvestmentRiskToleranceCategory
  assetAllocation: InvestmentAssetAllocationBreakdown
}

export enum UpdateInvestmentAssetAllocationErrorType {
  SELECTION_OUT_OF_RECOMMENDATION_BOUND = 'SELECTION_OUT_OF_RECOMMENDATION_BOUND',
  TOO_MANY_UPDATES = 'TOO_MANY_UPDATES',
}

export enum CashEquivalentCategory {
  TRIPLE_TAX_FREE = 'TRIPLE_TAX_FREE',
  NATIONAL = 'NATIONAL',
  CORPORATE = 'CORPORATE',
  TREASURY_BILL = 'TREASURY_BILL',
}

export interface UpdateInvestmentAssetAllocationError {
  errorType?: UpdateInvestmentAssetAllocationErrorType
}

export interface InvestmentCashEquivalentCategoriesRecommendationOutput {
  recommended: CashEquivalentCategory
  current: CashEquivalentCategory
  allowTripleTaxFreeMunicipalMutualFund: boolean
}

export interface InvestmentCashEquivalentConfig {
  __typename: 'InvestmentCashEquivalentConfig'
  cashEquivalentCategory: CashEquivalentCategory
}

export interface UpdateInvestmentCashEquivalentCategoriesError {
  __typename: 'UpdateInvestmentCashEquivalentCategoriesError'
  errorType: UpdateInvestmentCashEquivalentCategoriesErrorType
}
export enum UpdateInvestmentCashEquivalentCategoriesErrorType {
  INVALID_SELECTION = 'INVALID_SELECTION',
  TOO_MANY_UPDATES = 'TOO_MANY_UPDATES',
}

export type UpdateInvestmentCashEquivalentCategoriesOutput =
  | InvestmentCashEquivalentConfig
  | UpdateInvestmentCashEquivalentCategoriesError

export interface UpdateInvestmentCashEquivalentCategoriesInput {
  cashEquivalentCategory: CashEquivalentCategory
}

export interface InvestmentEquitiesSectorBreakdown {
  __typename?: 'InvestmentEquitiesSectorBreakdown'
  healthcare: number
  consumer: number
  financial: number
  technology: number
  industry: number
  energy: number
  realEstate: number
  media: number
}

export enum UpdateInvestmentEquitiesSectorsErrorType {
  PERCENTAGE_SUM_MISMATCH = 'PERCENTAGE_SUM_MISMATCH',
  SELECTION_OUT_OF_RECOMMENDATION_BOUND = 'SELECTION_OUT_OF_RECOMMENDATION_BOUND',
  TOO_MANY_UPDATES = 'TOO_MANY_UPDATES',
}

export interface UpdateInvestmentEquitiesSectorsError {
  __typename: 'UpdateInvestmentEquitiesSectorsError'
  errorType: UpdateInvestmentEquitiesSectorsErrorType
}

export type UpdateInvestmentEquitiesSectorsOutput =
  | InvestmentEquitiesSectorBreakdown
  | UpdateInvestmentEquitiesSectorsError

export interface UpdateInvestmentEquitiesSectorsInput {
  healthcare: number
  consumer: number
  financial: number
  technology: number
  industry: number
  energy: number
  realEstate: number
  media: number
}

export interface RecommendationPercentageRange {
  recommendedPercent: number
  allowedMinPercent: number
  allowedMaxPercent: number
  allowedMaxAllocationPercent: number
}

export interface InvestmentEquitiesSectorsRecommendationOutput {
  healthcare: RecommendationPercentageRange
  consumer: RecommendationPercentageRange
  financial: RecommendationPercentageRange
  technology: RecommendationPercentageRange
  industry: RecommendationPercentageRange
  energy: RecommendationPercentageRange
  realEstate: RecommendationPercentageRange
  media: RecommendationPercentageRange
  currentBreakdown: InvestmentEquitiesSectorBreakdown
}

export interface SectorSlider {
  value: number
  recommendedRange: RecommendationPercentageRange
}

export interface EquitySectorSlider extends SectorSlider {
  key: EquitiesSectorEnum
}

export interface FixedIncomeSlider extends SectorSlider {
  key: FixedIncomeCategoryEnum
}

export interface BaseEquitiesSector {
  healthcare: EquitySectorSlider
  consumer: EquitySectorSlider
  financial: EquitySectorSlider
  technology: EquitySectorSlider
  industry: EquitySectorSlider
  energy: EquitySectorSlider
  realEstate: EquitySectorSlider
  media: EquitySectorSlider
}

export interface BaseFixedIncomeCategory {
  governmentBonds: FixedIncomeSlider
  highYieldBonds: FixedIncomeSlider
  corporateBonds: FixedIncomeSlider
  municipalBonds: FixedIncomeSlider
  internationalBonds: FixedIncomeSlider
}

export enum EquitiesSectorEnum {
  HEALTHCARE = 'healthcare',
  CONSUMER = 'consumer',
  FINANCIAL = 'financial',
  TECHNOLOGY = 'technology',
  INDUSTRY = 'industry',
  ENERGY = 'energy',
  REAL_ESTATE = 'realEstate',
  MEDIA = 'media',
}

export enum FixedIncomeCategoryEnum {
  GOVERNMENT_BONDS = 'governmentBonds',
  HIGH_YIELD_BONDS = 'highYieldBonds',
  CORPORATE_BONDS = 'corporateBonds',
  MUNICIPAL_BONDS = 'municipalBonds',
  INTERNATIONAL_BONDS = 'internationalBonds',
}

export interface UpdateInvestmentFixedIncomeCategoriesError {
  __typename: 'UpdateInvestmentFixedIncomeCategoriesError'
  errorType: UpdateInvestmentFixedIncomeCategoriesErrorType
}

export enum UpdateInvestmentFixedIncomeCategoriesErrorType {
  PERCENTAGE_SUM_MISMATCH = 'PERCENTAGE_SUM_MISMATCH',
  SELECTION_OUT_OF_RECOMMENDATION_BOUND = 'SELECTION_OUT_OF_RECOMMENDATION_BOUND',
  TOO_MANY_UPDATES = 'TOO_MANY_UPDATES',
}

export interface InvestmentFixedIncomeCategoryBreakdown {
  __typename: 'InvestmentFixedIncomeCategoryBreakdown'
  governmentBonds: number
  highYieldBonds: number
  corporateBonds: number
  municipalBonds: number
  internationalBonds: number
}

export interface InvestmentFixedIncomeCategoriesRecommendationOutput {
  governmentBonds: RecommendationPercentageRange
  highYieldBonds: RecommendationPercentageRange
  corporateBonds: RecommendationPercentageRange
  municipalBonds: RecommendationPercentageRange
  internationalBonds: RecommendationPercentageRange
  currentBreakdown: InvestmentFixedIncomeCategoryBreakdown
}

export type UpdateInvestmentFixedIncomeCategoriesOutput =
  | InvestmentFixedIncomeCategoryBreakdown
  | UpdateInvestmentFixedIncomeCategoriesError

export interface UpdateInvestmentFixedIncomeCategoriesInput {
  governmentBonds: number
  highYieldBonds: number
  corporateBonds: number
  municipalBonds: number
  internationalBonds: number
}

export interface TradeConfirms {
  displayName: string
  fileKey: string
  alias: number
}

export interface InvestmentTradeConfirmsInput {
  alias: number
}

export interface InvestmentTradeConfirms {
  confirms: TradeConfirms[]
}

export interface InvestmentDocumentKey {
  displayName: string
  fileKey: string
  alias: number
  url?: string
}

export interface InvestmentTradeConfirmsOutput {
  confirms: InvestmentDocumentKey[]
}

export enum InvestmentAccountStatus {
  PENDING = 'PENDING',
  OPEN = 'OPEN',
  OPEN_NO_NEW_TRADES = 'OPEN_NO_NEW_TRADES',
  FROZEN = 'FROZEN',
  CLOSED = 'CLOSED',
}

export interface PendingJointAcatTransfersOutput {
  pendingJointAcatTransfers: PendingJointAcatTransfer[]
}

export interface PendingJointAcatTransfer {
  pendingAcatTransferAlias: number
  jointAccountHolderFirstName: string
  submittedFileNames: string[]
  transferType: JointAcatTransferType
  toAccountAlias: number
  status: PendingJointAcatTransferStatus
}

export enum PendingJointAcatTransferStatus {
  IN_JOINT_OWNER_REVIEW = 'IN_JOINT_OWNER_REVIEW', // returns if the initiator of the joint acat is calling the pendingJointAcatTransfers
  REQUIRES_REVIEW = 'REQUIRES_REVIEW', // returns if the joint owner of the account who needs to do the authorization/rejection calls pendingJointAcatTransfers
  AUTHORIZED = 'AUTHORIZED', // JOINT OWNER AUTHORIZED THE TRANSFER
  REJECTED = 'REJECTED', // JOINT OWNER REJECTED THE TRANSFER
}

export enum JointAcatTransferType {
  FULL = 'FULL',
  PARTIAL = 'PARTIAL',
}

export interface AuthorizeJointAcatTransferInput {
  pendingAcatTransferAlias: number
  disclosures: DisclosureInput[]
}

export interface RejectJointAcatTransferInput {
  pendingAcatTransferAlias: number
}

export type ReviewJointAcatTransferOutput = ReviewJointAcatTransferSuccess | ReviewJointAcatTransferOutputError

export interface ReviewJointAcatTransferSuccess {
  __typename: 'ReviewJointAcatTransferSuccess'
  isSuccessful: boolean
}

export interface ReviewJointAcatTransferOutputError {
  __typename: 'ReviewJointAcatTransferOutputError'
  errorType: ReviewJointAcatTransferOutputErrorType
}

export enum ReviewJointAcatTransferOutputErrorType {
  INITIATOR_CANNOT_DECISION_ACAT = 'INITIATOR_CANNOT_DECISION_ACAT', // new, jic initiator somehow tries to authorize/reject their own joint acat
  ACAT_ALREADY_DECISIONED = 'ACAT_ALREADY_DECISIONED', // joint acat has already been authorized/rejected
  REQUIRED_DISCLOSURE_NOT_ACKNOWLEDGED = 'REQUIRED_DISCLOSURE_NOT_ACKNOWLEDGED', // required disclosure not acknowledged
}
